/**
 * @lodex-pro/web-admin/features/ops/labels — plain words for the ops console (#56 PR 1b).
 * The API sends event and category codes; admins read these.
 */
import type { OpsCategory, OpsHealthStatus, OpsSeverity } from './hooks';

const EVENT_LABELS: Readonly<Record<string, string>> = {
  api_error: 'Server error',
  uncaught_error: 'Unexpected server error',
  fastify_transport_error: 'Request could not be handled',
  rate_limit_enforced: 'Rate limit hit',
  permission_denied: 'Permission denied',
  gps_rejected_zero_zero: 'GPS 0,0 rejected',
  email_send_failed: 'Email not sent',
  ses_bounce: 'Email bounced',
  ses_complaint: 'Email marked as spam',
  push_send_failed: 'Push notification failed',
  push_device_unregistered: 'Phone app removed (no more pushes to it)',
  run_ticket_pdf_failed: 'Run ticket PDF failed',
  driver_checkin_failed: 'Driver check-in not recorded',
  sync_conflict_parked: 'Sync conflict held for review',
  sync_orphan_parked: 'Sync item held (parent not found)',
  sync_unknown_job_parked: 'Sync item held (job not on the server yet)',
  import_commit_failed: 'Import failed',
  import_commit_kick_failed: 'Import did not start',
  import_commit_state_write_failed: 'Import status not saved',
  ops_events_dropped: 'Too many events — some were counted, not stored',
};

/** "shift_idle_sweep_failed" → "Shift idle sweep failed" for anything not listed. */
export function eventLabel(event: string): string {
  const known = EVENT_LABELS[event];
  if (known !== undefined) return known;
  const words = event.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const CATEGORY_LABELS: Readonly<Record<OpsCategory, string>> = {
  API: 'API',
  SYNC: 'Sync',
  EMAIL: 'Email',
  PUSH: 'Push',
  PDF: 'PDF',
  IMPORT: 'Import',
  CATALOG_SYNC: 'Catalog sync',
  JOB_PIPELINE: 'Jobs',
  SCHEDULER: 'Scheduled jobs',
  AUTH: 'Access',
};

export const OPS_CATEGORY_OPTIONS = Object.keys(CATEGORY_LABELS) as OpsCategory[];

export function categoryLabel(c: OpsCategory): string {
  return CATEGORY_LABELS[c] ?? c;
}

export type Tone = 'neutral' | 'positive' | 'muted' | 'accent' | 'danger' | 'warning';

/** A pino level number from the raw logs (30 info, 40 warn, 50 error, 60 fatal). */
export function logLevelLabel(level: string): string {
  const n = Number(level);
  if (n >= 60) return 'Fatal';
  if (n >= 50) return 'Error';
  if (n >= 40) return 'Warning';
  if (n >= 30) return 'Info';
  return Number.isFinite(n) ? 'Debug' : level;
}

export function logLevelTone(level: string): Tone {
  const n = Number(level);
  return n >= 50 ? 'danger' : n >= 40 ? 'warning' : 'muted';
}

export function severityTone(s: OpsSeverity): Tone {
  switch (s) {
    case 'CRITICAL':
    case 'ERROR':
      return 'danger';
    case 'WARNING':
      return 'warning';
    default:
      return 'muted';
  }
}

export function statusTone(s: OpsHealthStatus): Tone {
  return s === 'CRITICAL' ? 'danger' : s === 'WARNING' ? 'warning' : 'positive';
}

export const STATUS_LABEL: Readonly<Record<OpsHealthStatus, string>> = {
  HEALTHY: 'Healthy',
  WARNING: 'Needs attention',
  CRITICAL: 'Critical',
};

/** 0 → "—", 45 → "45 s", 600 → "10 min", 7200 → "2 h", 2 days → "2 d". */
export function ageLabel(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${String(seconds)} s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${String(mins)} min`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${String(hours)} h`;
  return `${String(Math.round(hours / 24))} d`;
}

export function timeAgo(iso: string | null, now: number = Date.now()): string {
  if (iso === null) return 'never';
  const secs = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  return secs < 30 ? 'just now' : `${ageLabel(secs)} ago`;
}

export type SearchTarget =
  | { readonly kind: 'refCode'; readonly value: string }
  | { readonly kind: 'errorId'; readonly value: string }
  | { readonly kind: 'requestId'; readonly value: string }
  | { readonly kind: 'none' }
  | { readonly kind: 'invalid' };

/** One search box: ERR-XXXX, err_<26>, or a request id; anything else is refused. */
export function parseSearch(input: string): SearchTarget {
  const v = input.trim();
  if (v === '') return { kind: 'none' };
  if (/^ERR-[0-9A-Z]{4}$/i.test(v)) return { kind: 'refCode', value: v.toUpperCase() };
  if (/^err_[0-9A-Z]{26}$/i.test(v)) {
    return { kind: 'errorId', value: `err_${v.slice(4).toUpperCase()}` };
  }
  if (/^req_[0-9A-Za-z_-]{1,76}$/.test(v)) return { kind: 'requestId', value: v };
  return { kind: 'invalid' };
}
